import React from 'react';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { 
  Building2,
  Globe,
  Shield,
  Zap,
  Users,
  BarChart3,
  Layers,
  ArrowRight,
  Check,
  Server,
  Lock,
  Palette,
  Settings,
  HeadphonesIcon,
  TrendingUp,
  Store,
  Database,
  Cloud,
  GitBranch,
  Workflow,
  Sparkles,
  Eye,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';

const Enterprise = () => {
  // Set SEO-optimized title for Enterprise page
  usePageTitle('enterprise');
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Enterprise features
  const features = [
    {
      icon: Globe,
      title: "Unlimited Scale",
      description: "Handle thousands of locations and millions of customers without breaking a sweat"
    },
    {
      icon: Palette,
      title: "White Label Ready",
      description: "Complete branding customization to match your enterprise identity"
    },
    {
      icon: Workflow,
      title: "Multi-Agent System",
      description: "Deploy specialized AI agents for customer service, recommendations, and insights"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "SOC 2 compliant with advanced security features and dedicated infrastructure"
    },
    {
      icon: HeadphonesIcon,
      title: "Dedicated Support",
      description: "24/7 priority support with dedicated account management"
    },
    {
      icon: Database,
      title: "Data Ownership",
      description: "Complete control and ownership of your data with custom retention policies"
    }
  ];


  // Enterprise clients (example logos)
  const clients = [
    "Darden Restaurants",
    "Brinker International",
    "Bloomin' Brands",
    "Restaurant Brands International",
    "Yum! Brands",
    "Inspire Brands"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section - Blue gradient background */}
      <section className="relative pt-32 pb-20 bg-[#2256e5] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/20 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left side - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1 flex items-center justify-center"
            >
              <div className="backdrop-blur-sm rounded-2xl p-4 shadow-2xl max-w-md w-full border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                <div className="aspect-square relative overflow-hidden rounded-xl">
                  <img 
                    src="/enterprise-dashboard.png"
                    alt="Enterprise Analytics Dashboard"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right side - Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerChildren}
              className="order-1 lg:order-2 text-left"
            >
              <motion.div variants={fadeInUp}>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-dineai-yellow/20 backdrop-blur-sm rounded-full mb-6">
                  <Building2 className="w-4 h-4 text-dineai-yellow" />
                  <span className="text-dineai-yellow font-medium text-sm">ENTERPRISE SOLUTIONS</span>
                </div>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-3xl md:text-4xl font-bold mb-6 text-white"
              >
                AI That Scales With Your{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Restaurant Empire
                </span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl mb-8 text-blue-50"
              >
                Learn how restaurant chains can manage thousands of locations 
                with our truly agentic architecture
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button
                  size="lg"
                  className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all"
                >
                  Schedule Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all"
                >
                  See AI in Action
                </Button>
              </motion.div>
              
              <motion.div 
                variants={fadeInUp}
                className="mt-8 flex flex-wrap gap-6 text-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#FDB913] rounded-full"></div>
                  <span>Deploy to thousands of locations</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#FDB913] rounded-full"></div>
                  <span>Increase Revenues by 15-20%</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise Challenges Section - Dark blue background */}
      <section className="py-20 bg-[#1f4ed1] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-6">
                <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  THE ENTERPRISE CHALLENGE
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Your Scale, Your{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Biggest Challenge
                </span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                Managing hundreds of locations means every problem is multiplied. 
                Every inefficiency costs millions. Every missed opportunity compounds.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Pain Point 1 - Inconsistent Experience */}
              <motion.div
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <motion.div 
                  className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4"
                  animate={{ 
                    rotate: [0, -10, 10, -10, 0],
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <Store className="h-8 w-8 text-[#1f4ed1]" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  500 Locations, 500 Experiences
                </h3>
                <p className="text-white/70">
                  Each location operates differently. Customers get frustrated 
                  with inconsistent service. Brand reputation suffers.
                </p>
              </motion.div>

              {/* Pain Point 2 - Labor Crisis */}
              <motion.div
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <motion.div 
                  className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4"
                  animate={{ 
                    scale: [1, 0.9, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 0.5
                  }}
                >
                  <Users className="h-8 w-8 text-[#1f4ed1]" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Labor Costs Out of Control
                </h3>
                <p className="text-white/70">
                  Training costs millions. Turnover is 75%+. Peak hours are 
                  understaffed. Quality suffers everywhere.
                </p>
              </motion.div>

              {/* Pain Point 3 - Data Blindness */}
              <motion.div
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <motion.div 
                  className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4"
                  animate={{ 
                    y: [0, -5, 0],
                    rotate: [0, -5, 5, 0]
                  }}
                  transition={{ 
                    duration: 3.5,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 1
                  }}
                >
                  <BarChart3 className="h-8 w-8 text-[#1f4ed1]" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Drowning in Data, Starving for Insights
                </h3>
                <p className="text-white/70">
                  Terabytes of data but no actionable insights. Can't compare 
                  locations. Missing revenue opportunities daily.
                </p>
              </motion.div>

              {/* Pain Point 4 - Lost Revenue */}
              <motion.div
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <motion.div 
                  className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4"
                  animate={{ 
                    rotate: [0, 360],
                  }}
                  transition={{ 
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <TrendingUp className="h-8 w-8 text-[#1f4ed1]" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Losing $ Millions Across Locations
                </h3>
                <p className="text-white/70">
                  Missed upsells. Phone tag instead of orders. Customers choosing 
                  competitors. The losses compound across every location.
                </p>
              </motion.div>
            </div>

            <motion.div 
              variants={fadeInUp}
              className="text-center mt-12"
            >
              <p className="text-2xl font-semibold text-white mb-4">
                Every Day Without AI Is Revenue Lost
              </p>
              <p className="text-lg text-white/70 max-w-3xl mx-auto">
                While you're managing fires at one location, 499 others are missing opportunities. 
                Your competitors using AI are capturing your customers.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-8"
            >
              <Button
                size="lg"
                className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all"
                onClick={() => {
                  document.getElementById('enterprise-features')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See How We Solve This
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all"
                onClick={() => {
                  document.getElementById('ai-operations-commander')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Peek Inside
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Enterprise Features Section - Dark blue background */}
      <section id="enterprise-features" className="py-20 bg-[#2256e5] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-6">
                <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  FEATURES
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Enterprise-Grade{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Features
                </span>
              </h2>
              <p className="text-xl text-white/70">
                Everything you need to transform your restaurant chain
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  variants={fadeInUp}
                  custom={index}
                  whileHover={{ 
                    y: -10,
                    scale: 1.02,
                    transition: { 
                      duration: 0.3,
                      ease: "easeOut"
                    }
                  }}
                  className="h-full"
                >
                  <Card className="relative bg-white/10 backdrop-blur-sm border border-white/20 p-6 h-full transition-all duration-300 hover:bg-white/20 hover:shadow-2xl hover:shadow-dineai-yellow/20 hover:border-dineai-yellow/50 overflow-hidden group">
                    {/* Animated gradient background */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-br from-dineai-yellow/0 via-dineai-yellow/5 to-dineai-yellow/0"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    <div className="relative flex flex-col items-center text-center">
                      <motion.div 
                        className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4 shadow-md"
                        whileHover={{ 
                          scale: 1.15, 
                          rotate: 360,
                          boxShadow: "0 0 25px rgba(253, 185, 19, 0.5)"
                        }}
                        transition={{ 
                          duration: 0.6,
                          rotate: {
                            duration: 0.8,
                            ease: "easeInOut"
                          }
                        }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          transition={{ duration: 0.3 }}
                        >
                          <feature.icon className="h-8 w-8 text-[#1f4ed1]" />
                        </motion.div>
                      </motion.div>
                      <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-dineai-yellow">
                        {feature.title}
                      </h3>
                      <p className="text-white/60 transition-colors duration-300 group-hover:text-white/90">
                        {feature.description}
                      </p>
                      
                      {/* Animated shine effect */}
                      <motion.div 
                        className="absolute top-0 -inset-x-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                        initial={{ x: "-200%" }}
                        whileHover={{ x: "200%" }}
                        transition={{ 
                          duration: 0.8,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Operations Commander - Product Preview */}
      <section id="ai-operations-commander" className="py-20 bg-[#1f4ed1] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  PEEK INSIDE
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Your 24/7 AI Operations{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Commander
                </span>
              </h2>
              <p className="text-xl text-white/70">
                Real-time intelligence across all locations, not another dashboard
              </p>
            </motion.div>

            {/* AI Intelligence Grid */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* AI Virtual Assistant Performance */}
              <motion.div 
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:shadow-xl hover:shadow-white/10"
                whileHover={{ 
                  scale: 1.01,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full">
                      <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        AI ASSISTANT PERFORMANCE
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Dine AI Virtual Assistant Impact Across 847 Locations
                  </h3>
                  
                  {/* Key Metrics - Subtle colored backgrounds */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <motion.div 
                      className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-lg p-4 border border-emerald-500/20 transition-all duration-300 hover:from-emerald-500/20 hover:to-green-500/20 hover:border-emerald-500/40 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.p 
                        className="text-3xl font-bold text-emerald-400"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >427K</motion.p>
                      <p className="text-white/60 text-sm">Questions Answered</p>
                    </motion.div>
                    <motion.div 
                      className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-lg p-4 border border-blue-500/20 transition-all duration-300 hover:from-blue-500/20 hover:to-cyan-500/20 hover:border-blue-500/40 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.p 
                        className="text-3xl font-bold text-blue-400"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >12.8K hrs</motion.p>
                      <p className="text-white/60 text-sm">Staff Time Saved</p>
                    </motion.div>
                    <motion.div 
                      className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-lg p-4 border border-yellow-500/20 transition-all duration-300 hover:from-yellow-500/20 hover:to-orange-500/20 hover:border-yellow-500/40 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.p 
                        className="text-3xl font-bold text-yellow-400"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >0.3s</motion.p>
                      <p className="text-white/60 text-sm">Avg Response Time</p>
                    </motion.div>
                    <motion.div 
                      className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg p-4 border border-purple-500/20 transition-all duration-300 hover:from-purple-500/20 hover:to-pink-500/20 hover:border-purple-500/40 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.p 
                        className="text-3xl font-bold text-purple-400"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                      >24/7</motion.p>
                      <p className="text-white/60 text-sm">Always Available</p>
                    </motion.div>
                  </div>
                  
                  {/* Cost Savings - Highlighted with stronger gradient */}
                  <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-lg p-4 border border-emerald-500/30">
                    <p className="text-emerald-400 font-bold text-2xl">$384K</p>
                    <p className="text-white/60 text-sm">Weekly labor cost savings</p>
                  </div>
                </div>

                {/* Top Questions - Simplified */}
                <div className="mt-4 bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-white/80 text-sm font-medium mb-3 flex items-center gap-2">
                    <span className="text-dineai-yellow">📊</span>
                    Top Customer Questions:
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                      <p className="text-white/70 text-sm">1. "Show me gluten-free options"</p>
                      <span className="text-white/40 text-sm">78,412×</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <p className="text-white/70 text-sm">2. "Cocktails that pair with tacos?"</p>
                      <span className="text-white/40 text-sm">65,987×</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <p className="text-white/70 text-sm">3. "$100 budget for 2 people?"</p>
                      <span className="text-white/40 text-sm">48,456×</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-dineai-yellow text-sm">
                      💡 Trend: "Pairing recommendations" up 62% - upsell opportunity!
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Perfect Meal Journeys - Visual Style */}
              <motion.div 
                variants={fadeInUp}
                className="bg-gradient-to-br from-blue-600/40 to-purple-600/40 backdrop-blur-md rounded-xl border border-white/20 p-6 transition-all duration-300 hover:from-blue-600/50 hover:to-purple-600/50 hover:border-white/30 hover:shadow-xl hover:shadow-purple-500/10"
                whileHover={{ 
                  scale: 1.01,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl font-bold text-dineai-yellow">#1</span>
                    <span className="bg-dineai-yellow/90 text-black px-3 py-1 rounded-full text-sm font-bold">
                      Hidden Bestseller
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">12,847</p>
                    <p className="text-white/70 text-sm">customers ordered</p>
                    <p className="text-white/70 text-sm">this last month</p>
                  </div>
                </div>

                {/* Visual Journey with Images */}
                <div className="flex items-center justify-between mb-6">
                  <motion.div 
                    className="text-center flex-1 cursor-pointer"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div 
                      className="w-24 h-24 mx-auto bg-white/10 rounded-lg overflow-hidden mb-2 flex items-center justify-center border border-white/20 transition-all duration-300 hover:border-dineai-yellow/50 hover:shadow-lg hover:shadow-dineai-yellow/20"
                      whileHover={{ rotate: -5 }}
                    >
                      <img 
                        src="/enterprise-cucumber-soup.png" 
                        alt="Cucumber Avocado"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<span class="text-3xl">🥑</span>';
                        }}
                      />
                    </motion.div>
                    <p className="text-white font-semibold text-sm">Cucumber</p>
                    <p className="text-white font-semibold text-sm">Avocado Soup</p>
                    <motion.p 
                      className="text-dineai-yellow text-lg font-bold"
                      whileHover={{ scale: 1.2 }}
                    >$9.12</motion.p>
                  </motion.div>
                  
                  <motion.div 
                    className="text-dineai-yellow text-2xl"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >→</motion.div>
                  
                  <motion.div 
                    className="text-center flex-1 cursor-pointer"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div 
                      className="w-24 h-24 mx-auto bg-white/10 rounded-lg overflow-hidden mb-2 flex items-center justify-center border border-white/20 transition-all duration-300 hover:border-dineai-yellow/50 hover:shadow-lg hover:shadow-dineai-yellow/20"
                      whileHover={{ rotate: 5 }}
                    >
                      <img 
                        src="/enterprise-quinoa-salad.png" 
                        alt="Quinoa Salad"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<span class="text-3xl">🥗</span>';
                        }}
                      />
                    </motion.div>
                    <p className="text-white font-semibold text-sm">Quinoa</p>
                    <p className="text-white font-semibold text-sm">Salad</p>
                    <motion.p 
                      className="text-dineai-yellow text-lg font-bold"
                      whileHover={{ scale: 1.2 }}
                    >$8.00</motion.p>
                  </motion.div>
                  
                  <motion.div 
                    className="text-dineai-yellow text-2xl"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  >→</motion.div>
                  
                  <motion.div 
                    className="text-center flex-1 cursor-pointer"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div 
                      className="w-24 h-24 mx-auto bg-white/10 rounded-lg overflow-hidden mb-2 flex items-center justify-center border border-white/20 transition-all duration-300 hover:border-dineai-yellow/50 hover:shadow-lg hover:shadow-dineai-yellow/20"
                      whileHover={{ rotate: -5 }}
                    >
                      <img 
                        src="/enterprise-watermelon-mojito.png" 
                        alt="Watermelon Mojito"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<span class="text-3xl">🍹</span>';
                        }}
                      />
                    </motion.div>
                    <p className="text-white font-semibold text-sm">Watermelon</p>
                    <p className="text-white font-semibold text-sm">Mojito</p>
                    <motion.p 
                      className="text-dineai-yellow text-lg font-bold"
                      whileHover={{ scale: 1.2 }}
                    >$13.31</motion.p>
                  </motion.div>
                </div>

                {/* Total and Action */}
                <div className="border-t border-white/20 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-white/70">Current total:</p>
                    <p className="text-3xl font-bold text-white">$30.43</p>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-3 mb-4">
                    <p className="text-white/80 text-sm flex items-start">
                      <span className="text-dineai-yellow mr-2">💡</span>
                      Consider offering as a discounted combo if these items have healthy margins. Even a small discount can incentivize this popular combination across all 847 locations.
                    </p>
                  </div>

                  <button className="w-full bg-dineai-yellow hover:bg-yellow-400 text-black font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
                    <span className="text-xl">🚀</span>
                    Launch This Combo Chain-Wide
                  </button>
                </div>

                {/* Revenue Impact */}
                <div className="mt-4 space-y-3">
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg p-3 border border-green-400/30">
                    <p className="text-green-400 font-bold text-lg">+$3.8M potential</p>
                    <p className="text-white/70 text-xs">Monthly revenue if launched across all locations</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-lg p-3 border border-blue-400/30">
                    <p className="text-blue-400 font-bold text-lg">23% higher ticket size</p>
                    <p className="text-white/70 text-xs">Average order value when customers choose these combos</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Top Performers Across Locations */}
            <motion.div 
              variants={fadeInUp}
              className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-yellow-400 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">This Week's Champions Across All Locations</h3>
                  <p className="text-white/70">$1.15M revenue from top 3 items</p>
                </div>
              </div>
              
              {/* Top Items Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Item 1 - Grilled Mahi Mahi */}
                <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute top-2 left-2 w-8 h-8 bg-dineai-yellow rounded-full flex items-center justify-center z-10">
                    <span className="text-black font-bold text-sm">1</span>
                  </div>
                  <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                    <img 
                      src="/enterprise-grilled-mahi.png"
                      alt="Grilled Mahi Mahi"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-4xl">🐟</span></div>';
                      }}
                    />
                  </div>
                  <h4 className="text-white font-semibold mb-1">Grilled Mahi Mahi</h4>
                  <p className="text-2xl font-bold text-emerald-400 mb-2">$28</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Locations</span>
                      <span className="text-white font-semibold">127/847</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Weekly Units</span>
                      <span className="text-white font-semibold">2,847</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Revenue</span>
                      <span className="text-emerald-400 font-semibold">$487K</span>
                    </div>
                  </div>
                </div>
                
                {/* Item 2 - Cosmopolitan */}
                <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute top-2 left-2 w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center z-10">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                    <img 
                      src="/enterprise-cosmopolitan.png"
                      alt="Cosmopolitan"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-4xl">🍸</span></div>';
                      }}
                    />
                  </div>
                  <h4 className="text-white font-semibold mb-1">The Cosmopolitan</h4>
                  <p className="text-2xl font-bold text-emerald-400 mb-2">$15</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Locations</span>
                      <span className="text-white font-semibold">98/847</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Weekly Units</span>
                      <span className="text-white font-semibold">2,156</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Revenue</span>
                      <span className="text-emerald-400 font-semibold">$367K</span>
                    </div>
                  </div>
                </div>
                
                {/* Item 3 - BBQ Jackfruit Sandwich */}
                <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                  <div className="absolute top-2 left-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center z-10">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                    <img 
                      src="/enterprise-bbq-jackfruit.png"
                      alt="BBQ Jackfruit Sandwich"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-4xl">🍔</span></div>';
                      }}
                    />
                  </div>
                  <h4 className="text-white font-semibold mb-1">BBQ Jackfruit</h4>
                  <p className="text-2xl font-bold text-emerald-400 mb-2">$14</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Locations</span>
                      <span className="text-white font-semibold">214/847</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Weekly Units</span>
                      <span className="text-white font-semibold">1,998</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Revenue</span>
                      <span className="text-emerald-400 font-semibold">$298K</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* AI Performance Insights - Enterprise Scale */}
            <motion.div 
              variants={fadeInUp}
              className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-white/10 rounded-lg">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">AI Performance Insights</h3>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">💰</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className="text-xl font-semibold text-white">Price Point Analysis</h4>
                      <span className="bg-dineai-yellow/20 text-dineai-yellow px-3 py-1 rounded-full text-sm font-medium">
                        Pricing
                      </span>
                    </div>
                    <p className="text-white/80 text-lg leading-relaxed">
                      Your top performing items across all 847 locations - The Grilled Mahi Mahi ($28), 
                      The Cosmopolitan ($15), and BBQ Jackfruit Sandwich ($14) - show strong performance 
                      in the $14-$28 range. This data from 427K customer interactions suggests your 
                      chain's sweet spot is premium-casual pricing. Consider repositioning items outside 
                      this range: items under $12 show 43% lower attachment rates, while items over $32 
                      have 67% slower turnover. Adjusting just 15% of your menu to this optimal range 
                      could yield +$8.4M annually.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex gap-6">
                    <div>
                      <p className="text-white/60 text-sm">Potential Revenue Impact</p>
                      <p className="text-2xl font-bold text-emerald-400">+$8.4M/year</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Locations Affected</p>
                      <p className="text-2xl font-bold text-white">847</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Implementation Time</p>
                      <p className="text-2xl font-bold text-dineai-yellow">2 weeks</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Seamless Integrations Section - Dark blue background */}
      <section className="py-20 bg-[#2256e5] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Seamless{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Integrations
                </span>
              </h2>
              <p className="text-xl text-blue-100">
                Works with your existing technology stack
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center">
              <p className="text-xl text-blue-100">
                Custom integrations available for any POS, CRM, or ERP system
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Your Brand, Our Technology Section - Light blue background */}
      <section className="py-20 bg-[#1f4ed1] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Your Brand, Our{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Technology
                </span>
              </h2>
              <p className="text-xl text-blue-100">
                Complete white-label solution with your branding throughout
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div variants={fadeInUp}>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-[#FDB913] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Custom Domain & Branding</h3>
                      <p className="text-blue-100">Your domain, your logo, your colors - everywhere</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-[#FDB913] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Branded Mobile Apps</h3>
                      <p className="text-blue-100">iOS and Android apps with your brand in app stores</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-[#FDB913] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Custom AI Personality</h3>
                      <p className="text-blue-100">Train AI to match your brand voice and values</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-[#FDB913] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Private Infrastructure</h3>
                      <p className="text-blue-100">Dedicated servers with your security requirements</p>
                    </div>
                  </li>
                </ul>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-8">
                  <div className="relative h-64 bg-gradient-to-br from-white/10 to-white/5 rounded-lg border border-white/20 overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 left-0 w-32 h-32 border-2 border-white rounded-full -translate-x-16 -translate-y-16"></div>
                      <div className="absolute bottom-0 right-0 w-48 h-48 border-2 border-white rounded-full translate-x-24 translate-y-24"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative h-full flex items-center justify-center">
                      <div className="text-center">
                        {/* Mock Device Frames */}
                        <div className="flex items-center justify-center gap-4 mb-4">
                          {/* Phone Mock */}
                          <div className="w-20 h-36 bg-white/20 rounded-lg border-2 border-white/30 p-2 backdrop-blur-sm">
                            <div className="w-full h-full bg-gradient-to-b from-[#FDB913]/20 to-transparent rounded">
                              <div className="pt-2 px-2">
                                <div className="w-8 h-8 bg-[#FDB913] rounded-full mx-auto mb-2"></div>
                                <div className="space-y-1">
                                  <div className="h-1 bg-white/40 rounded-full"></div>
                                  <div className="h-1 bg-white/30 rounded-full w-4/5"></div>
                                  <div className="h-1 bg-white/20 rounded-full w-3/5"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Tablet Mock */}
                          <div className="w-36 h-24 bg-white/20 rounded-lg border-2 border-white/30 p-2 backdrop-blur-sm">
                            <div className="w-full h-full bg-gradient-to-br from-[#FDB913]/20 to-transparent rounded">
                              <div className="p-2 flex gap-2">
                                <div className="w-6 h-6 bg-[#FDB913] rounded"></div>
                                <div className="flex-1 space-y-1">
                                  <div className="h-1 bg-white/40 rounded-full"></div>
                                  <div className="h-1 bg-white/30 rounded-full"></div>
                                  <div className="grid grid-cols-3 gap-1 mt-2">
                                    <div className="h-6 bg-white/10 rounded"></div>
                                    <div className="h-6 bg-white/10 rounded"></div>
                                    <div className="h-6 bg-white/10 rounded"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-xl font-semibold text-white">Your Brand Everywhere</p>
                        <p className="text-blue-100 mt-2 text-sm">Web, iOS, Android - Fully Customized</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ready to Transform Section - Dark blue background */}
      <section className="py-20 bg-[#2256e5] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Ready to Transform Your{" "}
              <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                Restaurant Chain?
              </span>
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl mb-8 text-blue-100"
            >
              Join the leading restaurant chains already using Dine AI to revolutionize their operations
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button
                size="lg"
                className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all"
              >
                Schedule Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all"
              >
                See AI in Action
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Enterprise;